<!-- src/components/layouts/SettingsDialog.vue -->
<template>
    <a-modal
        :visible="visible"
        :footer="false"
        :width="900"
        :fullscreen="isMobile"
        @cancel="handleClose"
        class="glass-settings-modal"
        :mask-style="{ backdropFilter: 'blur(10px)', background: 'rgba(0, 0, 0, 0.2)' }"
        :modal-style="{ background: 'transparent', padding: 0, boxShadow: 'none' }"
        :closable="false"
    >
        <div class="settings-container glass-panel">
            <!-- 侧边栏 -->
            <div class="sidebar">
                <div class="sidebar-header">
                    <div class="window-controls">
                        <span class="control-dot close" @click="handleClose"></span>
                        <span class="control-dot minimize"></span>
                        <span class="control-dot maximize"></span>
                    </div>
                    <h3 class="sidebar-title">设置</h3>
                </div>
                
                <div class="nav-menu">
                    <div 
                        v-for="item in menuItems" 
                        :key="item.key"
                        class="nav-item"
                        :class="{ active: currentTab === item.key }"
                        @click="currentTab = item.key"
                    >
                        <component :is="item.icon" class="nav-icon" />
                        <span class="nav-label">{{ item.label }}</span>
                    </div>
                </div>
                
                <div class="sidebar-footer">
                    <div class="user-brief">
                        <a-avatar :size="32" :image-url="userAvatarUrl">User</a-avatar>
                        <span class="username">{{ userInfo.username }}</span>
                    </div>
                </div>
            </div>

            <!-- 内容区域 -->
            <div class="content-area">
                <div class="content-header">
                    <h2 class="content-title">{{ currentTitle }}</h2>
                </div>
                <div class="content-body">
                    <transition name="fade-slide" mode="out-in">
                        <keep-alive>
                            <component :is="currentComponent" :user="userInfo" />
                        </keep-alive>
                    </transition>
                </div>
            </div>
        </div>
    </a-modal>
</template>

<script>
import { defineComponent, ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { 
    IconUser, IconFire, IconFolder, IconSettings, 
    IconCalendar, IconMessage, IconInfoCircle 
} from '@arco-design/web-vue/es/icon';

import UserInfo from "@/components/layouts/UserInfo.vue";
import GroupManagement from "@/components/layouts/GroupManagement.vue";
import Schedule from "@/components/layouts/Schedule.vue";
import Suggestions from "@/components/layouts/Suggestions.vue";
import Settings from "@/components/layouts/Settings.vue";
import About from "@/components/layouts/About.vue";
import HotRanking from "@/components/layouts/HotRanking.vue";

export default defineComponent({
    name: "SettingsDialog",
    components: {
        UserInfo, GroupManagement, Schedule, Suggestions, 
        Settings, About, HotRanking,
        IconUser, IconFire, IconFolder, IconSettings, 
        IconCalendar, IconMessage, IconInfoCircle
    },
    props: {
        visible: { type: Boolean, default: false },
    },
    emits: ['update:visible'],
    setup(props, { emit }) {
        const store = useStore();
        const currentTab = ref('userinfo');
        const isMobile = ref(false);

        const menuItems = [
            { key: 'userinfo', label: '我的信息', icon: 'icon-user', component: 'UserInfo' },
            { key: 'group', label: '分组管理', icon: 'icon-folder', component: 'GroupManagement' },
            { key: 'settings', label: '个性化', icon: 'icon-settings', component: 'Settings' },
            { key: 'hotranking', label: '热榜配置', icon: 'icon-fire', component: 'HotRanking' },
            { key: 'schedule', label: '进度管理', icon: 'icon-calendar', component: 'Schedule' },
            { key: 'suggestions', label: '反馈建议', icon: 'icon-message', component: 'Suggestions' },
            { key: 'about', label: '关于', icon: 'icon-info-circle', component: 'About' },
        ];

        const userInfo = computed(() => store.getters['user/currentUser'] || { username: 'Guest' });
        
        const userAvatarUrl = computed(() => {
            const user = userInfo.value;
            if (user && user.avatar) {
                return `${process.env.VUE_APP_BASE_URL || ''}${user.avatar}`;
            }
            return 'https://picsum.photos/50';
        });

        const currentComponent = computed(() => {
            const item = menuItems.find(i => i.key === currentTab.value);
            return item ? item.component : 'UserInfo';
        });

        const currentTitle = computed(() => {
            const item = menuItems.find(i => i.key === currentTab.value);
            return item ? item.label : '';
        });

        const handleClose = () => {
            emit('update:visible', false);
        };

        const handleResize = () => {
            isMobile.value = window.innerWidth < 768;
        };

        onMounted(() => {
            handleResize();
            window.addEventListener("resize", handleResize);
        });

        onBeforeUnmount(() => {
            window.removeEventListener("resize", handleResize);
        });

        return {
            currentTab,
            menuItems,
            currentComponent,
            currentTitle,
            isMobile,
            userInfo,
            userAvatarUrl,
            handleClose,
        };
    }
});
</script>

<style scoped>
.settings-container {
    display: flex;
    height: 600px;
    width: 100%;
    overflow: hidden;
    background: rgba(255, 255, 255, 0.75);
    backdrop-filter: blur(25px) saturate(180%);
    border-radius: 16px;
    box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
}

/* Sidebar Styling */
.sidebar {
    width: 240px;
    background: rgba(245, 245, 247, 0.5);
    border-right: 1px solid rgba(0, 0, 0, 0.06);
    display: flex;
    flex-direction: column;
    padding: 20px 12px;
}

.sidebar-header {
    margin-bottom: 24px;
    padding: 0 12px;
}

.window-controls {
    display: flex;
    gap: 8px;
    margin-bottom: 16px;
}

.control-dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    cursor: pointer;
}

.control-dot.close { background: #ff5f57; }
.control-dot.minimize { background: #febc2e; }
.control-dot.maximize { background: #28c840; }

.sidebar-title {
    font-size: 20px;
    color: var(--text-main);
    padding-left: 4px;
}

.nav-menu {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;
    overflow-y: auto;
}

.nav-item {
    display: flex;
    align-items: center;
    padding: 10px 12px;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.2s;
    color: var(--text-main);
    font-size: 14px;
    font-weight: 500;
}

.nav-item:hover {
    background: rgba(0, 0, 0, 0.04);
}

.nav-item.active {
    background: var(--primary-color);
    color: white;
    box-shadow: 0 2px 8px rgba(0, 122, 255, 0.25);
}

.nav-icon {
    margin-right: 10px;
    font-size: 16px;
}

.sidebar-footer {
    margin-top: 20px;
    padding-top: 16px;
    border-top: 1px solid rgba(0, 0, 0, 0.06);
    padding-left: 12px;
}

.user-brief {
    display: flex;
    align-items: center;
    gap: 10px;
}

.username {
    font-weight: 600;
    font-size: 14px;
    color: var(--text-main);
}

/* Content Area */
.content-area {
    flex: 1;
    display: flex;
    flex-direction: column;
    background: transparent;
    height: 100%;
}

.content-header {
    padding: 24px 32px 16px;
    border-bottom: 1px solid rgba(0, 0, 0, 0.04);
}

.content-title {
    font-size: 24px;
    color: var(--text-main);
}

.content-body {
    flex: 1;
    padding: 24px 32px;
    overflow-y: auto;
}

/* Transitions */
.fade-slide-enter-active,
.fade-slide-leave-active {
    transition: all 0.3s ease;
}

.fade-slide-enter-from {
    opacity: 0;
    transform: translateX(10px);
}

.fade-slide-leave-to {
    opacity: 0;
    transform: translateX(-10px);
}

/* Mobile Adaptation */
@media (max-width: 768px) {
    .settings-container {
        flex-direction: column;
        height: 100vh;
        border-radius: 0;
    }
    .sidebar {
        width: 100%;
        height: auto;
        padding: 16px;
        flex-direction: row;
        align-items: center;
        overflow-x: auto;
        border-right: none;
        border-bottom: 1px solid rgba(0,0,0,0.06);
    }
    .sidebar-header, .sidebar-footer {
        display: none;
    }
    .nav-menu {
        flex-direction: row;
    }
    .nav-item {
        white-space: nowrap;
    }
}
</style>

<style>
/* Reset Arco Modal Styles */
.glass-settings-modal .arco-modal {
    background: transparent !important;
    box-shadow: none !important;
}
.glass-settings-modal .arco-modal-body {
    padding: 0 !important;
}
</style>
